/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable no-param-reassign */
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import ApiServices from '@Src/apiServices';
import * as Sentry from '@sentry/react';
import { AUTH_ERROR, GenericError } from '@Src/commons/Constants';
import {
  TypeLoginWithIpAddress,
  TypeLoginWithMobile,
  TypeSignupFormValues,
  TypeSignupWithCaptcha,
  TypeUserAdminData,
} from '@Src/components/Auth/AuthProps';
import {
  decodeJwtToken,
  getAudSource,
  getJwtToken,
  getUserId,
  logOut,
  removeJwtToken,
  setJwtToken,
  setUserDetails,
  TypeUserData,
} from '@Src/commons/Utils/authUtils';
import {
  AuthVerifyApiResponse,
  AuthVerifyApiResponseNew,
  GenericResponseProp,
  TypeResponse1,
  TypeRestFormWithToken,
  TypeUIResource,
} from '@Src/apiServices/ApiProps/ServicesProps';
import {
  TypeCompanyCreation,
  TypeServiceActivationData,
} from '@Src/components/Onboarding/OnboardingProps';
import { TypeUIResourceData } from '@Src/commons/GlobalTypes';
import { GstinCredentialType } from '@Src/components/OrganisationProfile/Type';
import { getAppSource } from '@Src/commons/Utils';
import { logoutSessions } from '@Src/apiServices/authService';

interface AuthState {
  authToken: string | null;
  userData: TypeUserData;
  onboardingStatus: TypeServiceActivationData[];
  accessible_ui_resource: string;
}

const initialState: AuthState = {
  authToken: null,
  userData: {},
  onboardingStatus: [],
  accessible_ui_resource: '',
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    updateAuthToken: (state, action: PayloadAction<string | null>) => {
      state.authToken = action.payload;
    },
    updateUserDetails: (
      state,
      action: PayloadAction<{ jwtToken: string | null; userData: TypeUserData }>
    ) => {
      state.authToken = action.payload.jwtToken;
      state.userData = action.payload.userData;
    },
    updateOnboardingStatus: (
      state,
      action: PayloadAction<TypeServiceActivationData[]>
    ) => {
      state.onboardingStatus = action.payload || [];
    },
    updateAccessibleUIResource: (state, action: PayloadAction<string>) => {
      state.accessible_ui_resource = action.payload;
    },
  },
});

export const {
  updateAuthToken,
  updateUserDetails,
  updateOnboardingStatus,
  updateAccessibleUIResource,
} = authSlice.actions;

const addUserIdForGTM = (userId: string | null) => {
  window.dataLayer = window.dataLayer || [];
  const userIdExists = window.dataLayer.find((item) => item.user_id === userId);

  if (!userIdExists) {
    window.dataLayer.push({
      user_id: userId,
    });
  } else {
    userIdExists.user_id = userId;
  }
};

const addUserIdForCleverTap = (user: TypeUserData, isLogIn: boolean) => {
  const userPhoneNumber = user.phone_number ?? '';

  const formattedPhoneNumber =
    userPhoneNumber.length >= 10 ? `+91${userPhoneNumber.slice(-10)}` : '';

  // if (isLogIn) {
  //   clevertap.onUserLogin.push({
  //     Site: {
  //       Name: `${user.first_name ?? ''} ${user.last_name ?? ''}`.trim(), // String
  //       Identity: user.user_id, // String or number
  //       Email: user.email, // Email address of the user
  //       Phone: formattedPhoneNumber,
  //       CompanyId: user.company_id,
  //       Company: user.company_name,
  //       'MSG-email': true, // Disable email notifications
  //       'MSG-push': true, // Enable push notifications
  //       'MSG-sms': true, // Enable sms notifications
  //       'MSG-whatsapp': true, // Enable WhatsApp notifications
  //     },
  //   });
  // } else {
  //   clevertap.profile.push({
  //     Site: {
  //       Name: `${user.first_name ?? ''} ${user.last_name ?? ''}`.trim(), // String
  //       Identity: user.user_id, // String or number
  //       Email: user.email, // Email address of the user
  //       Phone: formattedPhoneNumber,
  //       CompanyId: user.company_id,
  //       Company: user.company_name,
  //       'MSG-email': true, // Disable email notifications
  //       'MSG-push': true, // Enable push notifications
  //       'MSG-sms': true, // Enable sms notifications
  //       'MSG-whatsapp': true, // Enable WhatsApp notifications
  //     },
  //   });
  // }
};

export const checkJwtToken = (isLogIn = false) => {
  try {
    const jwtToken = getJwtToken();
    if (!jwtToken) throw new Error(AUTH_ERROR.JWT_TOKEN_NOT_FOUND);

    const userData: TypeUserData = decodeJwtToken(jwtToken);
    setUserDetails(userData);
    Sentry.setTag('user_email', localStorage.getItem('email') || '');
    addUserIdForGTM(userData?.user_id?.toString() || null);
    // addUserIdForCleverTap(userData, isLogIn);

    return userData;
  } catch (error) {
    removeJwtToken();
    throw error;
  }
};

const handleLoginResponse = (response: AuthVerifyApiResponseNew) => {
  if (response.sessions_available) {
    setJwtToken(response.jwt_token);
    const userData = checkJwtToken(true);
    return {
      userData,
      jwtToken: response.jwt_token,
      sessionsAvailable: response.sessions_available,
    };
  }
  return {
    encryptedToken: response.encrypted_token,
    activeSessions: response.active_sessions,
    message: response.message,
    sessionsAvailable: response.sessions_available,
  };
};

export const loginAction = async (data: TypeLoginWithIpAddress) => {
  const response: AuthVerifyApiResponseNew = await ApiServices.loginAPI(data);
  return handleLoginResponse(response);
};

export const loginWithMobileAction = async (data: TypeLoginWithMobile) => {
  const response: AuthVerifyApiResponseNew =
    await ApiServices.loginWithMobileAPI(data);
  return handleLoginResponse(response);
};

export const simpleSignupAction = async (
  data: TypeSignupFormValues,
  captchToken?: string
) => {
  const tempData: TypeSignupWithCaptcha = {
    ...data,
    'g-recaptcha-response': captchToken,
  };
  const response: GenericResponseProp<AuthVerifyApiResponse> =
    await ApiServices.simpleSignupAPI(tempData);
  setJwtToken(response.data.jwt_token);
  const userData = checkJwtToken(true);
  return {
    userData,
    jwtToken: response.data.jwt_token,
  };
};

export const googleSignupAction = async (token: string) => {
  const response: AuthVerifyApiResponseNew = await ApiServices.googleSignupAPI(
    token
  );
  if (response.sessions_available) {
    setJwtToken(response.jwt_token);
    const userData = checkJwtToken(true);
    return {
      userData,
      jwtToken: response.jwt_token,
      sessionsAvailable: response.sessions_available,
    };
  }
  return {
    encryptedToken: response.encrypted_token,
    activeSessions: response.active_sessions,
    message: response.message,
    sessionsAvailable: response.sessions_available,
  };
};

export const refreshJWTToken = async () => {
  if (getAppSource() !== getAudSource()) {
    logoutAction();
  }
  const response: GenericResponseProp<AuthVerifyApiResponse> =
    await ApiServices.refreshJWTTokenAPI();
  setJwtToken(response.data.jwt_token);
  const userData = checkJwtToken();
  return {
    userData,
    jwtToken: response.data.jwt_token,
  };
};

export const updateUserDetailsAction = async (data: TypeUserAdminData) => {
  const response: GenericResponseProp<AuthVerifyApiResponse> =
    await ApiServices.updateUserAdminDataAPI(data);
  setJwtToken(response.data.jwt_token);
  const userData = checkJwtToken();
  return {
    userData,
    jwtToken: response.data.jwt_token,
  };
};

export const getUIResources = async (): Promise<TypeUIResourceData> => {
  const userId = getUserId();
  if (!userId) throw new Error(GenericError.ERR_USER_DETAILS_DECODE);
  const response: TypeUIResource = await ApiServices.getUIResourcesAPI(userId);

  const uiResourceData: TypeUIResourceData = {
    allResources: {},
    permittedResources: [],
  };
  if (response.resource_permission) {
    uiResourceData.permittedResources = response.resource_permission.map(
      (resource) => resource.resource
    );
  }
  if (response.all_resources) {
    Object.values(response.all_resources).forEach((resource) => {
      uiResourceData.allResources = {
        ...uiResourceData.allResources,
        ...resource,
      };
    });
  }
  return uiResourceData;
};

export const forgotPasswordApiAction = async (
  email: string,
  captcha: string
): Promise<string> => {
  const response: TypeResponse1 = await ApiServices.forgotPasswordApi(
    email,
    captcha
  );
  return response.message || '';
};

export const resetPasswordAction = async (data: TypeRestFormWithToken) => {
  const response: TypeResponse1 = await ApiServices.resetPasswordApi(data);
  return response.message || '';
};

export const validateResetPasswordToken = async (
  token: string
): Promise<string> => {
  const response: GenericResponseProp<string> =
    await ApiServices.validateResetPasswordTokenAPI(token);
  if (response.error) throw new Error(response.error);
  return response.data;
};

export const gstinLoginAction = async (data: GstinCredentialType) => {
  const response = await ApiServices.createCredentialAPI(data);
  if (response.error) throw new Error(response.error);
  return response.data;
};

export const chekGstinLoginAction = async (gstin: string) => {
  if (!gstin) {
    throw new Error('Invalid Gstin');
  }
  const response = await ApiServices.checkActiveGstinAPI(gstin);
  return response.isActive;
};

export const externalUserCreationInOneClickAction = async (
  userdata: TypeUserAdminData,
  authData: TypeSignupFormValues,
  companyData: TypeCompanyCreation
) => {
  const data = {
    firstName: userdata.first_name,
    lastName: userdata.last_name,
    contactNumber: userdata.phone_number,

    email: authData.email,
    password: authData.password,

    pan: companyData.pan,
    source: 'INSTA_INVOICE',
    countryShortName: 'IN',
    countryId: 105,
    company: {
      name: companyData.companyName,
    },
    primaryAddress: [
      {
        city: companyData.city,
        state: companyData.city,
        is_billing: true,
        country: companyData.country,
        street_address: companyData.street,
        pincode: companyData.pincode,
        address_type: 'billing',
        gstin: companyData?.gstin,
        gstin_state_code: companyData?.gstin?.substring(0, 2),
        tax_identification_type: 'pan',
        tax_identification_number: companyData?.pan,
      },
    ],
  };
  const response: GenericResponseProp<AuthVerifyApiResponse> =
    await ApiServices.oneClickSignupAPI(data);

  setJwtToken(response.data.jwt_token);
  const userData = checkJwtToken(true);
  return {
    userData,
    jwtToken: response.data.jwt_token,
  };
};

export const logoutAction = async () => {
  addUserIdForGTM(null);
  // clevertap.logout();
  try {
    await logoutSessions();
  } finally {
    logOut();
  }
};

export default authSlice.reducer;
